'use client'

import React, { Suspense } from 'react'
import { GetSimilarProjectsForTagsDocument, SimilarArtistProjectFragment } from '@/gql/generated/graphql'
import { AllPurposeLink } from '@components/all-purpose-link'
import { artistRoute, artistsRoute } from '@utils/routes'
import { ArrowRightIcon, BodyText, DisplayText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { safeModulo } from '@utils/math'
import { PaginationButton, PaginationDirection } from '@components/pagination-button'
import { AnimatePresence, motion } from 'motion/react'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { useQuery } from 'urql'
import { OverlineHeader } from '@components/overline-header'

const SimilarArtistItem = ({
  project,
  currArtistSlug
}: {
  project: SimilarArtistProjectFragment
  currArtistSlug?: string
}) => {
  const artistPage = project?.artistPage?.data?.[0] ?? null
  const artist = project?.artist
  const [hovered, setHovered] = React.useState(false)

  const slug = artistPage?.attributes?.slug ?? artist?.public_address
  const artistName = artistPage?.attributes?.artistName ?? artist?.display_name
  const token = project?.featured_token?.[0]?.media_url ?? ''

  const avatar = artistPage
    ? artistPage?.attributes?.avatar?.data?.attributes?.url ||
      artistPage?.attributes?.ReleasesSection?.projectMetadata?.[0]?.Project?.featured_token?.[0]?.media_url ||
      ''
    : artist?.profile_by_id?.profile_picture?.url || token

  return (
    <AllPurposeLink
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      href={artistRoute(slug)}
      onClick={() =>
        trackButtonClicked(TrackedButtonNames.similarArtist, {
          artistSlug: currArtistSlug,
          customProperties: {
            'Routed to artist full url': artistRoute(slug),
            'Routed to artist': slug
          }
        })
      }
      className="w-full"
    >
      <div className="relative aspect-square h-auto w-full">
        <AnimatePresence>
          <motion.div
            className="bg-surface-low-inverted absolute inset-0"
            key={`${artistName}${hovered ? '-featured-token' : '-hovered'}`}
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ImageWithCustomLoader
              src={(hovered ? token : avatar) || ''}
              alt={`${artistName} profile picture card`}
              fill
              className={hovered ? 'p-md object-contain drop-shadow-[3px_3px_6px_rgba(0,0,0,0.20)]' : 'object-cover'}
              sizes="33vw"
            />
          </motion.div>
        </AnimatePresence>
      </div>
      <BodyText className="text-on-surface-inverted mt-md text-center">{artistName}</BodyText>
    </AllPurposeLink>
  )
}

type SimilarProjectsSet<T extends string> = {
  [slug in T]: SimilarArtistProjectFragment
}

export const SimilarArtists = ({
  tags,
  artistAddress,
  currArtistSlug
}: {
  tags: string[]
  artistAddress?: string | null
  currArtistSlug?: string
}): React.ReactNode => {
  const [currIndex, setCurrIndex] = React.useState<number>(0)

  const changeItem = (direction: PaginationDirection) => {
    // This is a hack to prevent the index from going out of bounds if the user goes
    // right then index should increase until it gets to the end and then it should be reset
    // to the beginning, if left then it should decrease until it gets to 0 then reset
    // to the end
    setCurrIndex(safeModulo(currIndex + (direction === PaginationDirection.Right ? 1 : -1), projects.length))
  }

  const whereCriteria = tags.length > 0 ? { tags: { tag_name: { _in: tags } } } : {}

  const [res] = useQuery({
    query: GetSimilarProjectsForTagsDocument,
    variables: {
      projectWhere: {
        ...whereCriteria,
        artist_address: { _neq: artistAddress || '' }
      }
    }
  })

  const artistSet: SimilarProjectsSet<string> = {}
  res.data?.projects_metadata?.forEach((project) => {
    const slug = project.artistPage?.data?.[0]?.attributes?.slug ?? project.artist.public_address
    if (slug) {
      artistSet[slug] = project
    }
  })

  const projects = Object.values(artistSet) ?? []

  if (projects.length === 0) {
    return null
  }

  return (
    <div className="px-md bg-surface-inverted text-on-surface-inverted gap-y-xl py-2xl flex flex-col">
      <div>
        <OverlineHeader text="DISCOVER" />
        <div className="gap-x-md flex flex-col sm:flex-row sm:items-end">
          <DisplayText className="font-medium">More artists</DisplayText>
          <AllPurposeLink href={artistsRoute} className="text-on-surface-variant gap-x-xs mb-2xs flex items-center">
            <BodyText>View all</BodyText>
            <ArrowRightIcon className="w-sm h-sm fill-on-surface-variant" />
          </AllPurposeLink>
        </div>
      </div>
      <div className="sm:gap-xl lg:gap-md hidden w-full grid-cols-1 sm:grid sm:grid-cols-2 lg:grid-cols-4">
        {projects.slice(0, 4).map((project) => {
          return (
            <SimilarArtistItem
              currArtistSlug={currArtistSlug}
              project={project}
              key={`similar-artists-${project.id}`}
            />
          )
        })}
      </div>
      <div className="flex w-full items-center justify-between sm:hidden">
        <PaginationButton
          direction={PaginationDirection.Left}
          onClick={() => changeItem(PaginationDirection.Left)}
          className="bg-primary-inverted-10 rounded-full"
        />
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.3
            }}
            key={projects[currIndex]?.id}
            className="px-sm w-full"
          >
            <Suspense>
              <SimilarArtistItem project={projects[currIndex]} />
            </Suspense>
          </motion.div>
        </AnimatePresence>
        <PaginationButton
          direction={PaginationDirection.Right}
          onClick={() => changeItem(PaginationDirection.Right)}
          className="bg-primary-inverted-10 rounded-full"
        />
      </div>
    </div>
  )
}
